import { useState } from 'react'

const securityFeatures = [
  {
    icon: '🔐',
    title: 'Enterprise Authentication',
    description: 'JWT tokens + Google OAuth 2.0 with 7-day expiration',
  },
  {
    icon: '🔒',
    title: 'Military-Grade Encryption',
    description: 'TLS 1.3 in transit, AES-256 at rest for all data',
  },
  {
    icon: '🛡️',
    title: 'GCP Infrastructure',
    description: 'SOC 2, ISO 27001, GDPR compliant infrastructure',
  },
  {
    icon: '🎯',
    title: 'Zero Vendor Lock-in',
    description: 'You own all resources - manage via GCP Console anytime',
  },
  {
    icon: '📦',
    title: 'Isolated Deployments',
    description: 'Your code runs on your GCP project, not our servers',
  },
  {
    icon: '🔄',
    title: 'Automatic Backups',
    description: 'Daily DB backups + point-in-time recovery (7 days)',
  },
]

const dataStorage = [
  { type: 'Source Code (ZIP)', location: 'GCS Bucket (Mumbai)', retention: '30 days', encryption: 'AES-256' },
  { type: 'Docker Images', location: 'Artifact Registry', retention: 'Until deleted', encryption: 'AES-256' },
  { type: 'Database', location: 'Cloud SQL (Mumbai)', retention: 'Until deleted', encryption: 'AES-256' },
  { type: 'Environment Vars', location: 'Cloud Run Secrets', retention: 'Until deleted', encryption: 'AES-256' },
  { type: 'User Accounts', location: 'PostgreSQL', retention: 'Until account deletion', encryption: 'bcrypt + AES-256' },
]

const faqs = [
  {
    question: 'Where is my code stored?',
    answer: 'Your code is uploaded directly to Google Cloud Storage (your bucket) in Mumbai. Inflynx backend never stores or runs your code - it only orchestrates the deployment process. All data is encrypted with AES-256.',
  },
  {
    question: 'What happens if Inflynx goes down?',
    answer: 'Your deployed apps continue running on Google Cloud Run completely independently. You can\'t deploy new versions, but existing apps are unaffected. You can manage them directly via GCP Console.',
  },
  {
    question: 'Do you use my code for AI training?',
    answer: 'No. We explicitly do not use your code for AI/ML training, share data with third parties, or sell usage analytics. We only scan code for security vulnerabilities during deployment.',
  },
  {
    question: 'How do I delete my data?',
    answer: 'Use `flynt deployments delete <id>` for deployments, `flynt projects delete <name>` for projects, or contact support@inflynx.cloud to delete your entire account. Deletions are immediate for deployments, 30 days for accounts.',
  },
  {
    question: 'Can I access my data directly?',
    answer: 'Yes! You own all GCP resources created through Inflynx. You can manage Cloud Run services, download code from GCS, export databases, and view logs via GCP Console or gcloud CLI.',
  },
  {
    question: 'What compliance certifications do you have?',
    answer: 'Inflynx runs on GCP which is SOC 2 Type II, ISO 27001, ISO 27017, ISO 27018, GDPR, HIPAA, and PCI DSS compliant.',
  },
  {
    question: 'Who can access my data?',
    answer: 'Only you. We use JWT tokens (7-day expiry) and Google OAuth 2.0 for authentication. All data is encrypted with AES-256 at rest and TLS 1.3 in transit.',
  },
  {
    question: 'What if a deployment fails?',
    answer: 'Build failures save logs for debugging. Deploy failures trigger automatic rollback to the previous version (zero downtime). Database crashes have <60s automatic failover with no data loss.',
  },
  {
    question: 'Is there vendor lock-in?',
    answer: 'None. You own all GCP resources (Cloud Run, Cloud SQL, GCS buckets). You can manage everything directly via GCP Console or gcloud CLI without needing Inflynx.',
  },
  {
    question: 'How secure is my data?',
    answer: 'Enterprise-grade: AES-256 encryption, bcrypt password hashing, VPC-private deployments, gVisor container sandboxing, and minimal IAM permissions. All infrastructure is GCP-managed.',
  },
]

export default function Security() {
  const [openFaq, setOpenFaq] = useState(null)

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">
            Trust & Security
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Transparent answers to your security questions. Your code and data are protected with enterprise-grade security.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-slate-200">
            <div className="text-4xl font-bold text-blue-600 mb-2">AES-256</div>
            <div className="text-slate-600">Encryption Standard</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-slate-200">
            <div className="text-4xl font-bold text-green-600 mb-2">99.9%</div>
            <div className="text-slate-600">Uptime SLA</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-slate-200">
            <div className="text-4xl font-bold text-purple-600 mb-2">7 Days</div>
            <div className="text-slate-600">Backup Retention</div>
          </div>
        </div>

        {/* Security Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8">
            Security Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md p-6 border border-slate-200 hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Data Storage Table */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8">
            Where Your Data is Stored
          </h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Data Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Retention
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Encryption
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {dataStorage.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                        {item.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {item.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {item.retention}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {item.encryption}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Architecture Diagram */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8">
            Security Architecture
          </h2>
          <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
              <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200 flex-1 max-w-xs">
                <div className="text-3xl mb-2">💻</div>
                <div className="font-semibold text-slate-900">Your CLI</div>
                <div className="text-sm text-slate-600">flynt deploy</div>
              </div>
              
              <div className="text-slate-400 text-2xl">→</div>
              
              <div className="bg-purple-50 rounded-lg p-6 border-2 border-purple-200 flex-1 max-w-xs">
                <div className="text-3xl mb-2">🚀</div>
                <div className="font-semibold text-slate-900">Inflynx Backend</div>
                <div className="text-sm text-slate-600">Orchestration only</div>
              </div>
              
              <div className="text-slate-400 text-2xl">→</div>
              
              <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200 flex-1 max-w-xs">
                <div className="text-3xl mb-2">☁️</div>
                <div className="font-semibold text-slate-900">Your GCP Project</div>
                <div className="text-sm text-slate-600">You own everything</div>
              </div>
            </div>
            
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-lg font-semibold text-slate-900">Cloud Run</div>
                <div className="text-sm text-slate-600">Your deployed app</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-lg font-semibold text-slate-900">Cloud SQL</div>
                <div className="text-sm text-slate-600">Your database</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-lg font-semibold text-slate-900">GCS Bucket</div>
                <div className="text-sm text-slate-600">Your code</div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  <span className={`transform transition-transform text-2xl ${openFaq === index ? 'rotate-45' : ''}`}>
                    +
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-slate-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-lg mb-6 opacity-90">
            We're here to help with any security or privacy concerns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:security@inflynx.cloud"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
            >
              security@inflynx.cloud
            </a>
            <a
              href="mailto:support@inflynx.cloud"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
            >
              support@inflynx.cloud
            </a>
          </div>
          <div className="mt-6 text-sm opacity-75">
            Security issues: 24h response • General support: 48h response
          </div>
        </div>
      </div>
    </div>
  )
}
